/**
 * Whether a CSS media query matches, kept current as it starts and stops matching (#111).
 *
 * Almost everything that differs on a phone is CSS, over one DOM. This hook is for the few places
 * where the markup itself has to differ, such as DataTable's cards, and for effects that must end
 * when the layout changes under them, such as the open drawer.
 *
 * **Always ask the phone condition, in the literal the stylesheets use** (`(max-width: 600px)`,
 * `(max-width: 900px)`; see the tier table in tokens.css). The answer is `false` wherever
 * `matchMedia` is missing, which includes jsdom, so "missing" has to mean "desktop". A query written
 * the other way round (`min-width`) would put every test in the phone layout.
 */

import { useCallback, useSyncExternalStore } from 'react'

const query = (media: string): MediaQueryList | null =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(media) : null

export function useMediaQuery(media: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = query(media)
      list?.addEventListener('change', onChange)
      return () => list?.removeEventListener('change', onChange)
    },
    [media],
  )
  return useSyncExternalStore(
    subscribe,
    () => query(media)?.matches ?? false,
    () => false,
  )
}
